// src/data/comparisons.ts
//
// Registry for "Fyrris vs. X" comparison pages, served by /compare/:slug.
//
// TO ADD A COMPETITOR:
//   1. Add one entry below with a URL-safe slug.
//   2. Fill in the copy and the rows. Leave status: "draft" until it is done —
//      drafts render with <meta name="robots" content="noindex"> automatically.
//   3. Flip status to "published", then add "/compare/<slug>" to
//      scripts/route-map.mjs so it is prerendered and enters the sitemap.
//
// A NOTE ON WRITING THESE. Comparison pages make factual claims about other
// companies' products, which is a legal and reputational surface, not just an
// SEO one. Two rules:
//   - Only state things verifiable from the competitor's own public material,
//     and date-stamp with `claimsVerifiedOn` when you check.
//   - Prefer "Fyrris does X" over "they cannot do X". Capability claims about
//     someone else's roadmap age badly.
// Nothing here is pre-filled with competitor claims for exactly that reason.

export type ComparisonStatus = "draft" | "published";

/** How a product handles a given capability. */
export type Support = "yes" | "no" | "partial" | "unknown";

export interface ComparisonRow {
  /** Capability being compared, phrased neutrally. */
  feature: string;
  fyrris: { support: Support; note?: string };
  competitor: { support: Support; note?: string };
}

export interface Comparison {
  slug: string;
  /** Competitor display name, as they write it. */
  competitorName: string;
  status: ComparisonStatus;

  /** 50-60 chars, primary keyword near the front. */
  metaTitle: string;
  /** 150-160 chars, ends with a CTA. */
  metaDescription: string;

  /** Single H1 for the page. */
  headline: string;
  /** 2-3 sentences. Written to stand alone if an engine quotes it. */
  summary: string;

  rows: ComparisonRow[];

  /** ISO date the competitor claims were last checked against public sources. */
  claimsVerifiedOn: string | null;
}

export const COMPARISONS: Comparison[] = [
  {
    slug: "example-competitor",
    competitorName: "[Competitor]",
    status: "draft",

    // Length targets are enforced by the page, not by convention — see
    // Compare.tsx, which warns in dev when these fall outside range.
    metaTitle: "Fyrris vs [Competitor] — AR for Fireplace Retailers",
    metaDescription:
      "TODO(copy): 150-160 characters comparing Fyrris with [Competitor] for hearth, patio and barbecue retailers, ending with a clear call to action.",

    headline: "Fyrris vs [Competitor]",
    summary:
      "TODO(copy): 2-3 sentences that answer 'which should I choose and why' on their own. This is the block an AI engine will quote, so it must not depend on the table below.",

    rows: [
      {
        feature: "TODO(copy): capability, phrased neutrally",
        fyrris: { support: "yes", note: "TODO(copy)" },
        competitor: { support: "unknown", note: "TODO(copy): verify from public sources" },
      },
      {
        feature: "TODO(copy)",
        fyrris: { support: "yes" },
        competitor: { support: "unknown" },
      },
    ],

    claimsVerifiedOn: null,
  },
];

export const getComparison = (slug?: string) =>
  COMPARISONS.find((c) => c.slug === slug) ?? null;

export const publishedComparisons = () =>
  COMPARISONS.filter((c) => c.status === "published");
